import { useState, useEffect } from 'react'
import { X, Plus, TrendingUp, TrendingDown, Minus, AlertTriangle, Building2, BarChart3, PieChart } from 'lucide-react'
import { Business, TLBHScore } from '@/types'
import { businessService } from '@/services/businessService'

interface BusinessComparisonData {
  business: Business
  score: TLBHScore
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  trend: 'improving' | 'stable' | 'declining'
  alerts: number
}

interface ScoreComparisonProps {
  businesses: BusinessComparisonData[]
  onAddBusiness: () => void
  onRemoveBusiness: (businessId: string) => void
}

export function ScoreComparison({ businesses, onAddBusiness, onRemoveBusiness }: ScoreComparisonProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'dimensions' | 'trends'>('overview')
  const [historicalData, setHistoricalData] = useState<Record<string, any[]>>({})
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  // Load historical data when switching to trends view
  useEffect(() => {
    if (viewMode === 'trends' && businesses.length > 0) {
      loadHistoricalData()
    }
  }, [viewMode, businesses])

  const loadHistoricalData = async () => {
    setIsLoadingHistory(true)
    try {
      const historyPromises = businesses.map(async (business) => {
        try {
          const response = await businessService.getScoreHistory(business.business.id)
          return { businessId: business.business.id, history: response.history || [] }
        } catch (error) {
          console.error(`Failed to load history for ${business.business.id}:`, error)
          return { businessId: business.business.id, history: [] }
        }
      })
      
      const results = await Promise.all(historyPromises)
      const historyMap: Record<string, any[]> = {}
      results.forEach(result => {
        historyMap[result.businessId] = result.history
      })
      setHistoricalData(historyMap)
    } catch (error) {
      console.error('Failed to load historical data:', error)
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const getBandColor = (band: string) => {
    switch (band) {
      case 'A+':
      case 'A': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'B+':
      case 'B': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900'
      case 'C+':
      case 'C': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'D': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'high': return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900'
      case 'critical': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'declining': return <TrendingDown className="w-4 h-4 text-red-600" />
      case 'stable': return <Minus className="w-4 h-4 text-gray-600" />
      default: return null
    }
  }

  const dimensionNames = {
    legal: 'Legal',
    financial: 'Financial',
    operational: 'Operational',
    market: 'Market'
  }

  const maxBusinesses = 4

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Score Comparison
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Compare TLBH scores and risk profiles across multiple businesses
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'overview'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setViewMode('dimensions')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'dimensions'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <BarChart3 className="w-4 h-4 mr-1" />
              Dimensions
            </button>
            <button
              onClick={() => setViewMode('trends')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'trends'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Trends
            </button>
          </div>
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Business Headers */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 font-medium text-gray-900 dark:text-white">
                Metrics
              </div>
              {Array.from({ length: maxBusinesses }).map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-700">
                  {businesses[index] ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900 dark:text-white truncate">
                            {businesses[index].business.name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {businesses[index].business.industry}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveBusiness(businesses[index].business.id)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={onAddBusiness}
                      className="w-full h-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Business
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Overview Mode */}
            {viewMode === 'overview' && (
              <>
                {/* TLBH Score Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                    TLBH Score
                  </div>
                  {Array.from({ length: maxBusinesses }).map((_, index) => (
                    <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                      {businesses[index] ? (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {businesses[index].score.tlbhScore.toFixed(2)}
                          </div>
                          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBandColor(businesses[index].score.band)} mt-1`}>
                            {businesses[index].score.band}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Risk Level Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                    Risk Level
                  </div>
                  {Array.from({ length: maxBusinesses }).map((_, index) => (
                    <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                      {businesses[index] ? (
                        <div className="text-center">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(businesses[index].riskLevel)}`}>
                            {businesses[index].riskLevel.charAt(0).toUpperCase() + businesses[index].riskLevel.slice(1)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Confidence Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                    Confidence
                  </div>
                  {Array.from({ length: maxBusinesses }).map((_, index) => (
                    <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                      {businesses[index] ? (
                        <div className="text-center">
                          <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            {parseFloat(businesses[index].score.confidence.toString()).toFixed(0)}%
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${parseFloat(businesses[index].score.confidence.toString())}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Trend Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                    Trend
                  </div>
                  {Array.from({ length: maxBusinesses }).map((_, index) => (
                    <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                      {businesses[index] ? (
                        <div className="flex items-center justify-center space-x-2">
                          {getTrendIcon(businesses[index].trend)}
                          <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                            {businesses[index].trend}
                          </span>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Alerts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                  <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                    Active Alerts
                  </div>
                  {Array.from({ length: maxBusinesses }).map((_, index) => (
                    <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                      {businesses[index] ? (
                        <div className="text-center">
                          {businesses[index].alerts > 0 ? (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {businesses[index].alerts}
                            </div>
                          ) : (
                            <div className="text-green-600 dark:text-green-400 font-medium">
                              No Alerts
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Dimensions Mode */}
            {viewMode === 'dimensions' && (
              <>
                {Object.entries(dimensionNames).map(([key, name]) => (
                  <div key={key} className="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                    <div className="p-4 font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                      {name}
                    </div>
                    {Array.from({ length: maxBusinesses }).map((_, index) => (
                      <div key={index} className="p-4 border-l border-gray-200 dark:border-gray-700">
                        {businesses[index] ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                {parseFloat(((businesses[index].score.dimensionScores as any)[key]?.score || 0).toString()).toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {((businesses[index].score.dimensionScores as any)[key]?.weight * 100 || 0).toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full"
                                style={{ 
                                  width: `${(businesses[index].score.dimensionScores as any)[key]?.score || 0}%` 
                                }}
                              />
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {(businesses[index].score.dimensionScores as any)[key]?.explanation || ''}
                            </p>
                          </div>
                        ) : (
                          <div className="text-center text-gray-400 dark:text-gray-600">-</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}

            {/* Trends Mode */}
            {viewMode === 'trends' && (
              <div className="p-6">
                {isLoadingHistory ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading historical data...</p>
                  </div>
                ) : businesses.length === 0 ? (
                  <div className="text-center py-8">
                    <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No Businesses Selected
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Add businesses to view their historical score trends.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Chart Legend */}
                    <div className="flex flex-wrap items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                      {businesses.map((business, index) => {
                        const colors = [
                          { line: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.1)' }, // blue
                          { line: 'rgb(16, 185, 129)', bg: 'rgba(16, 185, 129, 0.1)' }, // green
                          { line: 'rgb(245, 158, 11)', bg: 'rgba(245, 158, 11, 0.1)' }, // amber
                          { line: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)' }    // red
                        ]
                        const color = colors[index % colors.length]
                        return (
                          <div key={business.business.id} className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: color.line }}
                            />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {business.business.name}
                            </span>
                          </div>
                        )
                      })}
                    </div>

                    {/* Simple Line Chart */}
                    <div className="relative h-80 bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                      <svg className="w-full h-full" viewBox="0 0 800 300" preserveAspectRatio="none">
                        {/* Grid lines */}
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <g key={i}>
                            <line
                              x1="0"
                              y1={i * 60}
                              x2="800"
                              y2={i * 60}
                              stroke="currentColor"
                              strokeWidth="1"
                              className="text-gray-300 dark:text-gray-700"
                              strokeDasharray="4 4"
                            />
                            <text
                              x="5"
                              y={i * 60 - 5}
                              className="text-xs fill-gray-500 dark:fill-gray-400"
                            >
                              {100 - (i * 20)}
                            </text>
                          </g>
                        ))}

                        {/* Plot lines for each business */}
                        {businesses.map((business, index) => {
                          const history = historicalData[business.business.id] || []
                          if (history.length === 0) return null

                          const colors = [
                            'rgb(59, 130, 246)',  // blue
                            'rgb(16, 185, 129)',  // green
                            'rgb(245, 158, 11)',  // amber
                            'rgb(239, 68, 68)'    // red
                          ]
                          const color = colors[index % colors.length]

                          // Sort by timestamp and take last 12 months
                          const sortedHistory = [...history]
                            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                            .slice(-12)

                          if (sortedHistory.length < 2) return null

                          // Calculate points
                          const points = sortedHistory.map((record, i) => {
                            const x = (i / (sortedHistory.length - 1)) * 800
                            const y = 300 - ((parseFloat(record.score) / 100) * 300)
                            return { x, y, score: parseFloat(record.score) }
                          })

                          // Create path
                          const pathData = points.map((point, i) => 
                            `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                          ).join(' ')

                          return (
                            <g key={business.business.id}>
                              {/* Line */}
                              <path
                                d={pathData}
                                fill="none"
                                stroke={color}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* Points */}
                              {points.map((point, i) => (
                                <circle
                                  key={i}
                                  cx={point.x}
                                  cy={point.y}
                                  r="4"
                                  fill={color}
                                  className="hover:r-6 transition-all cursor-pointer"
                                >
                                  <title>{`${business.business.name}: ${point.score.toFixed(2)}`}</title>
                                </circle>
                              ))}
                            </g>
                          )
                        })}
                      </svg>

                      {/* X-axis labels */}
                      <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>12 months ago</span>
                        <span>6 months ago</span>
                        <span>Current</span>
                      </div>
                    </div>

                    {/* Trend Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {businesses.map((business) => {
                        const history = historicalData[business.business.id] || []
                        if (history.length === 0) {
                          return (
                            <div key={business.business.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 dark:text-white mb-2 truncate">
                                {business.business.name}
                              </h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400">No historical data</p>
                            </div>
                          )
                        }

                        const sortedHistory = [...history]
                          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                        
                        const oldest = parseFloat(sortedHistory[0]?.score || '0')
                        const newest = parseFloat(sortedHistory[sortedHistory.length - 1]?.score || '0')
                        const change = newest - oldest
                        const changePercent = oldest > 0 ? ((change / oldest) * 100) : 0

                        const scores = sortedHistory.map(h => parseFloat(h.score))
                        const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length
                        const maxScore = Math.max(...scores)
                        const minScore = Math.min(...scores)

                        return (
                          <div key={business.business.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-3 truncate">
                              {business.business.name}
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Change:</span>
                                <span className={`font-semibold ${change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                  {change >= 0 ? '+' : ''}{change.toFixed(2)} ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%)
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Average:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {avgScore.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Range:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {minScore.toFixed(2)} - {maxScore.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Volatility:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {(maxScore - minScore).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Insights */}
      {businesses.length > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Comparison Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">
                Highest Score
              </h4>
              {(() => {
                const highest = businesses.reduce((prev, current) => 
                  prev.score.tlbhScore > current.score.tlbhScore ? prev : current
                )
                return (
                  <div>
                    <p className="text-green-800 dark:text-green-200 font-semibold">
                      {highest.business.name}
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      Score: {highest.score.tlbhScore.toFixed(2)} ({highest.score.band})
                    </p>
                  </div>
                )
              })()}
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <h4 className="font-medium text-red-900 dark:text-red-100 mb-2">
                Highest Risk
              </h4>
              {(() => {
                const riskOrder = { low: 1, medium: 2, high: 3, critical: 4 }
                const highestRisk = businesses.reduce((prev, current) => 
                  riskOrder[prev.riskLevel] > riskOrder[current.riskLevel] ? prev : current
                )
                return (
                  <div>
                    <p className="text-red-800 dark:text-red-200 font-semibold">
                      {highestRisk.business.name}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      Risk: {highestRisk.riskLevel.charAt(0).toUpperCase() + highestRisk.riskLevel.slice(1)}
                    </p>
                  </div>
                )
              })()}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                Most Improving
              </h4>
              {(() => {
                const improving = businesses.filter(b => b.trend === 'improving')
                if (improving.length > 0) {
                  const best = improving[0] // Could be more sophisticated
                  return (
                    <div>
                      <p className="text-blue-800 dark:text-blue-200 font-semibold">
                        {best.business.name}
                      </p>
                      <p className="text-blue-600 dark:text-blue-400 text-sm">
                        Trend: Improving
                      </p>
                    </div>
                  )
                } else {
                  return (
                    <p className="text-blue-600 dark:text-blue-400 text-sm">
                      No businesses showing improvement
                    </p>
                  )
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}